import React, { useContext, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { UserProfileCardContext } from '../../store/context';

const TIMEOUT_DELAY = 2500;

const messages = defineMessages({
	copyEmailTooltip: {
		id: 'user-profile-card.EmailSection.copyEmailTooltip',
		defaultMessage: 'Copy email',
		description:
			'Tooltip shown on the button that copies the profiled user\u2019s email address to the clipboard',
	},
	copiedTooltip: {
		id: 'user-profile-card.EmailSection.copiedTooltip',
		defaultMessage: 'Copied',
		description:
			'Tooltip shown after the user\u2019s email address has been successfully copied to the clipboard',
	},
	copyEmailButtonLabel: {
		id: 'user-profile-card.EmailSection.copyEmailButtonLabel',
		defaultMessage: 'Copy email address',
		description: 'Accessible label for the button that copies the email address to the clipboard',
	},
	successIconLabel: {
		id: 'user-profile-card.EmailSection.successIconLabel',
		defaultMessage: 'Email copied',
		description: 'Accessible label for the checkmark icon confirming the email was copied',
	},
});

const styles = cssMap({
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	copyButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '20px',
		width: '20px',
		opacity: 0,
		borderRadius: token('radius.small'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: '0',
		borderStyle: 'none',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&:focus-within': {
			opacity: 1,
		},
	},
	// Revealed while the row is hovered or the button is in its copied-success state.
	copyButtonVisible: {
		opacity: 1,
	},
	copyButtonCopied: {
		cursor: 'default',
	},
});

interface EmailSectionProps {
	email: string;
	hideCopyButton?: boolean;
}

export function EmailSection({
	email,
	hideCopyButton = false,
}: EmailSectionProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { getTestId } = useContext(UserProfileCardContext);
	const [isCopied, setIsCopied] = useState(false);
	const [isRowHovered, setIsRowHovered] = useState(false);
	const updateTooltip = useRef<() => void>();
	const resetTimeout = useRef<ReturnType<typeof setTimeout>>();

	React.useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [isCopied]);

	React.useEffect(
		() => () => {
			if (resetTimeout.current) {
				clearTimeout(resetTimeout.current);
			}
		},
		[],
	);

	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(email);
		} catch {
			return;
		}

		setIsCopied(true);
		resetTimeout.current = setTimeout(() => {
			setIsCopied(false);
		}, TIMEOUT_DELAY);
	};

	const tooltipLabel = isCopied
		? formatMessage(messages.copiedTooltip)
		: formatMessage(messages.copyEmailTooltip);

	const isButtonRevealed = isRowHovered || isCopied;

	if (hideCopyButton) {
		return (
			<Box xcss={styles.row} testId={getTestId('email')}>
				<Text maxLines={1} color="color.text.subtlest" testId={getTestId('email-text')}>
					{email}
				</Text>
			</Box>
		);
	}

	return (
		<Box
			xcss={styles.row}
			testId={getTestId('email')}
			onMouseEnter={() => setIsRowHovered(true)}
			onMouseLeave={() => setIsRowHovered(false)}
		>
			<Text maxLines={1} color="color.text.subtlest" testId={getTestId('email-text')}>
				{email}
			</Text>
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;
					return tooltipLabel;
				}}
				position="top"
				hasNewContentOnTriggerClick
			>
				<Pressable
					isDisabled={isCopied}
					onClick={copyToClipboard}
					testId={getTestId('email-copy-button')}
					aria-label={formatMessage(messages.copyEmailButtonLabel)}
					xcss={cx(
						styles.copyButton,
						isButtonRevealed && styles.copyButtonVisible,
						isCopied && styles.copyButtonCopied,
					)}
				>
					{isCopied ? (
						<StatusSuccessIcon
							label={formatMessage(messages.successIconLabel)}
							color={token('color.icon.success')}
						/>
					) : (
						<CopyIcon label="" color={token('color.icon.subtle')} />
					)}
				</Pressable>
			</Tooltip>
		</Box>
	);
}
